import type { Term, BehaviourPointTotals } from '../types';

/** Today's Europe/London calendar date as yyyy-mm-dd. The canonical "school day"
 *  date across the app — never use toISOString() (that's UTC and rolls over at
 *  the wrong time in the evening). */
export function londonToday(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/London' });
}

/** The term whose [start, end] window (both inclusive) contains the given
 *  yyyy-mm-dd date, or null if none is configured for it. yyyy-mm-dd strings
 *  compare correctly lexicographically. */
export function termForDate(terms: Term[] | undefined | null, ymd: string): Term | null {
  if (!terms) return null;
  for (const t of terms) {
    if (t.start <= ymd && ymd <= t.end) return t;
  }
  return null;
}

/** The term containing today (Europe/London), or null. */
export function currentTerm(terms: Term[] | undefined | null): Term | null {
  return termForDate(terms, londonToday());
}

/**
 * Start of the current academic year — 1 September, this year or last,
 * depending on which side of September we're on. Used where "so far this year"
 * has to mean the school year, not the calendar year.
 *
 * Deliberately not driven by `pastoralConfig.terms`: term dates are optional,
 * only cover the behaviour module, and a school that hasn't filled them in
 * would otherwise get no totals at all.
 */
export function academicYearStart(now: Date = new Date()): Date {
  const parts = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/London' }));
  const year = parts.getMonth() >= 8 ? parts.getFullYear() : parts.getFullYear() - 1;
  return new Date(year, 8, 1, 0, 0, 0, 0); // 1 September, local midnight
}

/** Academic year containing a date, as '2025/26'. Must match `academicYearKey`
 *  in functions/src/behaviour — the stored totals are stamped with it. */
export function academicYearKey(now: Date = new Date()): string {
  const parts = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/London' }));
  const startYear = parts.getMonth() >= 8 ? parts.getFullYear() : parts.getFullYear() - 1;
  return `${startYear}/${String((startYear + 1) % 100).padStart(2, '0')}`;
}

/**
 * The four point figures for a pupil, with stale buckets zeroed.
 *
 * The trigger stamps the totals with the day and academic year they belong to,
 * so nothing has to run at midnight: yesterday's "today" simply reads as zero.
 * Always go through this rather than reading `behaviourPoints` directly.
 */
export function readBehaviourPoints(p: BehaviourPointTotals | null | undefined): {
  todayPos: number; todayNeg: number; yearPos: number; yearNeg: number;
} {
  const today = londonToday();
  const year = academicYearKey();
  const sameDay = !!p && p.todayDate === today;
  const sameYear = !!p && p.yearKey === year;
  return {
    todayPos: sameDay ? (p.todayPos ?? 0) : 0,
    todayNeg: sameDay ? (p.todayNeg ?? 0) : 0,
    yearPos: sameYear ? (p!.yearPos ?? 0) : 0,
    yearNeg: sameYear ? (p!.yearNeg ?? 0) : 0,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Term-date editing (School Settings). Pure helpers, no Firestore.
// ─────────────────────────────────────────────────────────────────────────────

const YMD = /^\d{4}-\d{2}-\d{2}$/;

/** The academic year a date belongs to, as its starting calendar year. */
export function academicYearOf(ymd: string): number {
  const y = Number(ymd.slice(0, 4));
  return Number(ymd.slice(5, 7)) >= 9 ? y : y - 1;
}

export function academicYearLabel(startYear: number): string {
  return `${startYear}/${String(startYear + 1).slice(2)}`;
}

/** The six half-terms most English schools run, with no dates — a scaffold
 *  for the admin to fill in, not a guess at their calendar. */
export function typicalYear(): Term[] {
  return ['Autumn 1', 'Autumn 2', 'Spring 1', 'Spring 2', 'Summer 1', 'Summer 2'].map((name) => ({ name, start: '', end: '' }));
}

/** Last year's dates moved on 52 weeks, so every date lands on the same
 *  weekday. A starting point to adjust, which is how most schools do it. */
export function shiftTerms(terms: Term[], weeks: number): Term[] {
  const shift = (ymd: string) => {
    if (!YMD.test(ymd)) return ymd;
    const d = new Date(`${ymd}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + weeks * 7);
    return d.toISOString().slice(0, 10);
  };
  return terms.map((t) => ({ name: t.name, start: shift(t.start), end: shift(t.end) }));
}

export interface TermDateProblem { index: number | null; message: string }

/**
 * Everything that would make the saved dates misleading rather than merely
 * incomplete: a term with one date but not the other, an end before its
 * start, two terms that overlap, an INSET day that is not a date or sits
 * inside a term (it is then just a working day). Blank rows are dropped, not
 * reported — the admin may be part-way through.
 */
export function validateTermDates(terms: Term[], insetDays: string[]): { terms: Term[]; insetDays: string[]; problems: TermDateProblem[] } {
  const problems: TermDateProblem[] = [];
  const kept: Term[] = [];
  terms.forEach((t, index) => {
    const name = t.name.trim();
    const hasStart = YMD.test(t.start), hasEnd = YMD.test(t.end);
    if (!name && !hasStart && !hasEnd) return;
    if (!hasStart || !hasEnd) { problems.push({ index, message: `${name || `Term ${index + 1}`} needs both a start and an end date.` }); return; }
    if (t.end < t.start) { problems.push({ index, message: `${name || `Term ${index + 1}`} ends before it starts.` }); return; }
    kept.push({ name: name || `Term ${index + 1}`, start: t.start, end: t.end });
  });
  kept.sort((a, b) => a.start.localeCompare(b.start));
  for (let i = 1; i < kept.length; i += 1) {
    if (kept[i]!.start <= kept[i - 1]!.end) problems.push({ index: null, message: `${kept[i - 1]!.name} and ${kept[i]!.name} overlap.` });
  }
  const days = [...new Set(insetDays.filter((d) => YMD.test(d)))].sort();
  for (const d of days) {
    if (kept.some((t) => t.start <= d && d <= t.end)) problems.push({ index: null, message: `INSET day ${d} falls inside a term — it only matters when it adjoins a holiday.` });
  }
  return { terms: kept, insetDays: days, problems };
}
